import { DateTime } from 'luxon'

import {
  DATE_FORMAT_FOR_API,
  SPOT_CONTRACT_LAST_POSSIBLE_DATE_MONTHS_OFFSET,
  START_DATE_OFFSET_NO_POA,
  START_DATE_OFFSET_POA,
} from '@/open-web/utils/constants'
import { CONTRACT_VALIDITY_KIND } from '@/open-web/utils/contractUtils'
import type { CampaignConfigurationEntry } from '@/shared/contentful/types'
import type { PriceAreaCode } from '@/shared/graphql/schema/commonBackend/graphql'
import type { EnrichedContractTemplate } from '@/shared/services/campaignDataResolver'
import type { CheckoutDataState } from '@/shared/store/slices/checkoutDataSlice'
import { logError } from '@/shared/utils/error'
import { SalesCampaignMisconfigurationError } from '@/shared/utils/errorClasses'
import { isNotNullOrUndefined } from '@/shared/utils/isNotNullOrUndefined'
import { TARIFF_ELEMENT_TYPE_PREFIX } from '@/shared/utils/tariffElementUtils'

/**
 * Utility function to calculate max contract start date.
 * For SPOT contracts it current date plus 14 months
 * For Fixed and Hybrid Contracts its base on the latest price period returned by the campaign data API.
 */
export const getLastContractStartDate = (
  contract: EnrichedContractTemplate,
  area: PriceAreaCode,
) => {
  try {
    const spotContractLastDate = DateTime.now().plus({
      months: SPOT_CONTRACT_LAST_POSSIBLE_DATE_MONTHS_OFFSET,
    })

    //SPOT contract have only one price per area
    if (contract.validity?.kind === CONTRACT_VALIDITY_KIND.CONTINUOUS) {
      return spotContractLastDate
    }

    const priceAreaObject = contract.tariffElements.find(
      (element) => element.type === TARIFF_ELEMENT_TYPE_PREFIX.energy,
    )?.pricesByArea

    if (!priceAreaObject) {
      throw logError(
        new SalesCampaignMisconfigurationError(
          'PricesByArea Object not found in Contract Template',
          {
            campaignId: contract.campaignId,
            tariffNo: contract.tariffNo,
          },
        ),
      )
    }

    const datesArray = priceAreaObject[area]
      ?.map(({ fromDate, toDate }) => ({ fromDate, toDate }))
      .filter(Boolean)

    const firstPeriodInArray = datesArray?.shift()

    if (!firstPeriodInArray) {
      throw logError(
        new SalesCampaignMisconfigurationError(
          'Prices for specified area not found in Contract Template',
          {
            campaignId: contract.campaignId,
            tariffNo: contract.tariffNo,
          },
        ),
      )
    }

    let lastValidStartDate = DateTime.fromISO(firstPeriodInArray.fromDate).endOf('month')
    datesArray?.forEach((item) => {
      const current = DateTime.fromISO(item.fromDate).endOf('month')
      if (current > lastValidStartDate) {
        lastValidStartDate = current
      }
    })

    if (lastValidStartDate < DateTime.now()) {
      logError(
        new SalesCampaignMisconfigurationError(
          `Outdated price list, latest valid period is before current date - ${lastValidStartDate}. Prices require an update.`,
          {
            campaignId: contract.campaignId,
            tariffNo: contract.tariffNo,
          },
        ),
      )
    }
    return lastValidStartDate
  } catch (error) {
    throw logError(
      new SalesCampaignMisconfigurationError(error, {
        campaignId: contract.campaignId,
        tariffNo: contract.tariffNo,
      }),
    )
  }
}

export const getFirstPossibleStartDateOffset = (
  isPoaGranted: boolean,
  contractConfiguration?: CampaignConfigurationEntry,
) => {
  if (isPoaGranted) {
    return isNotNullOrUndefined(contractConfiguration?.startDateOffsetWithPoa)
      ? contractConfiguration.startDateOffsetWithPoa
      : START_DATE_OFFSET_POA
  }
  return isNotNullOrUndefined(contractConfiguration?.startDateOffsetWithoutPoa)
    ? contractConfiguration.startDateOffsetWithoutPoa
    : START_DATE_OFFSET_NO_POA
}

export const getDefaultContractPeriod = (
  selectedStartDate: string | undefined,
  isPoaGranted: boolean,
  contractTemplate?: EnrichedContractTemplate,
  campaignConfiguration?: CampaignConfigurationEntry,
) => {
  const defaultStartDate = DateTime.now()
    .plus({
      days: getFirstPossibleStartDateOffset(isPoaGranted, campaignConfiguration),
    })
    .toFormat(DATE_FORMAT_FOR_API)

  const startDate = selectedStartDate || defaultStartDate
  const isDefaultStartDate = !selectedStartDate

  if (isNotNullOrUndefined(contractTemplate?.validity?.duration?.amount)) {
    const endDate = DateTime.fromFormat(startDate, DATE_FORMAT_FOR_API)
      .plus({
        months: contractTemplate.validity.duration.amount,
      })
      .endOf('month')
      .toFormat(DATE_FORMAT_FOR_API)
    return { startDate, endDate, isDefaultStartDate }
  }

  return { startDate, endDate: null, isDefaultStartDate }
}

/**
 * Get the contract termination date, to show in the UI
 * Important: This logic is applied only for the Finnish delivery step,
 * We need to substract one day from the start date, because at this date new contract will start, and that is the first day when old contract is terminated.
 * @param contractTermination value from checkoutData.contractTermination
 * @param startDate delivery start date
 * @returns string formatted date for contract termination or null if not set
 */
export const getContractTerminationDate = (
  contractTermination?: CheckoutDataState['contractTermination'],
  startDate?: string,
) => {
  if (contractTermination?.isSameAsStartDate && startDate) {
    return DateTime.fromFormat(startDate, DATE_FORMAT_FOR_API)
      .minus({ days: 1 })
      .toFormat(DATE_FORMAT_FOR_API)
  }

  return contractTermination?.date
}
